(() => {
  'use strict';

  const ATTRIBUTION_KEY = 'mplus_enquiry_attribution_v1';
  const PENDING_KEY = 'mplus_enquiry_pending_v1';
  const SUCCESS_KEY = 'mplus_enquiry_success_v1';
  const campaignKeys = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'gclid',
    'fbclid',
  ];

  const storage = {
    get(key) {
      try {
        return JSON.parse(window.sessionStorage.getItem(key) || 'null');
      } catch (_error) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.sessionStorage.setItem(key, JSON.stringify(value));
      } catch (_error) {
        // Tracking must never prevent a form from working.
      }
    },
  };

  function safePagePath(value) {
    if (!value) return '';

    try {
      const url = new URL(value, window.location.origin);
      return `${url.origin}${url.pathname}`;
    } catch (_error) {
      return '';
    }
  }

  function createTrackingId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }

    return `mplus-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  function pushEvent(eventName, details = {}) {
    const payload = { event: eventName, ...details };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    document.dispatchEvent(new CustomEvent('mplus:tracking', { detail: payload }));
  }

  function readAttribution() {
    const params = new URLSearchParams(window.location.search);
    const attribution = storage.get(ATTRIBUTION_KEY) || {};

    if (!attribution.landing_page) {
      attribution.landing_page = window.location.pathname;
    }

    if (!attribution.referrer && document.referrer) {
      attribution.referrer = safePagePath(document.referrer);
    }

    campaignKeys.forEach((key) => {
      const value = params.get(key);
      if (value) attribution[key] = value.slice(0, 250);
    });

    storage.set(ATTRIBUTION_KEY, attribution);
    return attribution;
  }

  function findFieldValue(form, names) {
    const field = Array.from(form.elements).find((element) => names.includes(element.name));
    return field && typeof field.value === 'string' ? field.value.trim() : '';
  }

  function addHiddenField(form, name, value) {
    if (!value) return;

    const existing = Array.from(form.elements).find((element) => element.name === name);
    if (existing) {
      if (!existing.value) existing.value = value;
      return;
    }

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    input.dataset.mplusTrackingField = 'true';
    form.appendChild(input);
  }

  function formSource(form) {
    return (
      findFieldValue(form, [
        'contact[Enquiry Source]',
        'contact[Form Source]',
        'contact[form_source]',
        'contact[Form]',
        'contact[Page]',
        'contact[subject]',
        'contact[Request type]',
      ]) || document.title || 'Website enquiry'
    );
  }

  function formIdentifier(form, index) {
    return form.id || form.getAttribute('class') || `contact-form-${index + 1}`;
  }

  function isContactForm(form) {
    const formType = form.querySelector('input[name="form_type"]');
    return formType ? formType.value === 'contact' : form.action.includes('/contact');
  }

  function hasVisibleSuccess(form) {
    return Boolean(
      form.querySelector(
        '[class*="__success"], [class*="--success"], [id*="-success-"], [data-form-success]'
      )
    );
  }

  function reportSuccess(form, index) {
    const pending = storage.get(PENDING_KEY) || {};
    const source = pending.source || formSource(form);
    const trackingId = pending.tracking_id || findFieldValue(form, ['contact[Tracking ID]']) || createTrackingId();
    const reported = storage.get(SUCCESS_KEY) || [];

    if (reported.includes(trackingId)) return;

    reported.push(trackingId);
    storage.set(SUCCESS_KEY, reported.slice(-20));
    storage.set(PENDING_KEY, null);

    const details = {
      form_id: pending.form_id || formIdentifier(form, index),
      enquiry_source: source,
      page_path: window.location.pathname,
      tracking_id: trackingId,
    };

    pushEvent('mplus_form_success', details);
    pushEvent('mplus_generate_lead', details);
  }

  function prepareForms(attribution) {
    const forms = Array.from(document.forms).filter(isContactForm);
    const successInUrl = new URLSearchParams(window.location.search).get('contact_posted') === 'true';

    forms.forEach((form, index) => {
      if (form.dataset.mplusEnquiryTracking === 'true') return;
      form.dataset.mplusEnquiryTracking = 'true';

      const source = formSource(form);
      const trackingId = createTrackingId();
      const formId = formIdentifier(form, index);

      addHiddenField(form, 'contact[Enquiry Source]', source);
      addHiddenField(form, 'contact[Landing Page]', attribution.landing_page);
      addHiddenField(form, 'contact[Current Page]', window.location.pathname);
      addHiddenField(form, 'contact[Referrer]', attribution.referrer);
      addHiddenField(form, 'contact[UTM Source]', attribution.utm_source);
      addHiddenField(form, 'contact[UTM Medium]', attribution.utm_medium);
      addHiddenField(form, 'contact[UTM Campaign]', attribution.utm_campaign);
      addHiddenField(form, 'contact[UTM Term]', attribution.utm_term);
      addHiddenField(form, 'contact[UTM Content]', attribution.utm_content);
      addHiddenField(form, 'contact[GCLID]', attribution.gclid);
      addHiddenField(form, 'contact[FBCLID]', attribution.fbclid);
      addHiddenField(form, 'contact[Tracking ID]', trackingId);

      let started = false;
      const reportStart = () => {
        if (started) return;
        started = true;
        pushEvent('mplus_form_start', {
          form_id: formId,
          enquiry_source: source,
          page_path: window.location.pathname,
        });
      };

      form.addEventListener('input', reportStart, { passive: true });
      form.addEventListener('change', reportStart, { passive: true });
      form.addEventListener('submit', () => {
        const submission = {
          form_id: formId,
          source,
          page_path: window.location.pathname,
          tracking_id: findFieldValue(form, ['contact[Tracking ID]']) || trackingId,
        };
        storage.set(PENDING_KEY, submission);
        pushEvent('mplus_form_submit', {
          form_id: formId,
          enquiry_source: source,
          page_path: window.location.pathname,
          tracking_id: submission.tracking_id,
        });
      });

      if (hasVisibleSuccess(form) || (successInUrl && index === 0)) {
        reportSuccess(form, index);
      }
    });
  }

  function prepareClickTracking() {
    document.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element)) return;
        const link = event.target.closest('a[href]');
        if (!link) return;

        const href = link.getAttribute('href') || '';
        const label = (link.textContent || link.getAttribute('aria-label') || '').trim().replace(/\s+/g, ' ').slice(0, 100);

        if (href.startsWith('tel:')) {
          pushEvent('mplus_phone_click', { page_path: window.location.pathname, link_label: label || 'Phone' });
          return;
        }

        if (href.startsWith('mailto:')) {
          pushEvent('mplus_email_click', { page_path: window.location.pathname, link_label: label || 'Email' });
          return;
        }

        if (/\.pdf(?:$|[?#])/i.test(href)) {
          pushEvent('mplus_download_click', { page_path: window.location.pathname, link_label: label || 'Download' });
          return;
        }

        if (/button|cta/i.test(link.className) || /check my land|free land check|get quote|book|schedule|contact/i.test(label)) {
          let targetPath = '';
          try {
            targetPath = new URL(href, window.location.origin).pathname;
          } catch (_error) {
            targetPath = href.startsWith('#') ? href : '';
          }

          pushEvent('mplus_cta_click', {
            page_path: window.location.pathname,
            link_label: label,
            target_path: targetPath,
          });
        }
      },
      true
    );
  }

  function init() {
    const attribution = readAttribution();
    prepareForms(attribution);
    prepareClickTracking();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
